import { useState, useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { formatPrice } from '../../utils/formatters';
import api from '../../services/api';
import ListLoadError from '../../components/admin/ListLoadError';
import styles from './GiftCardsPage.module.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getStatusClass = (status) => {
  if (status === 'redeemed') return styles.redeemed;
  if (status === 'void') return styles.void;
  return styles.active;
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  }
};

const GiftCardsPage = ({ section = 'all' }) => {
  // The Offers & Credits hub renders this page once per tab and asks for one
  // half. Any other value — including the default the standalone
  // /admin/gift-cards route still passes — renders BOTH, so that route is
  // unchanged. Gift cards and gifted memberships are separate systems that only
  // share this page; the split is what gives each its own tab.
  const showCredit = section !== 'memberships';
  const showMemberships = section !== 'credit';

  const { toast } = useToast();

  // Card #6238 c4. These two money surfaces rendered Generate/Void for ANY role
  // that could reach the page. The keys mirror what the admin router actually
  // enforces (`config/adminPermissionCodes.js`): POST -> `<module>.create`,
  // PATCH -> `<module>.edit`. `/gifted-memberships` maps to the SAME
  // `gift_cards` module in PERMISSION_MAP, so it shares these gates.
  const { hasPermission } = useAuth();
  const canCreateGift = hasPermission('gift_cards.create');
  const canVoidGift = hasPermission('gift_cards.edit');

  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [membershipsError, setMembershipsError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState('');
  const [lastGenerated, setLastGenerated] = useState(null);

  // Gifted free memberships
  const [tiers, setTiers] = useState([]);
  const [giftedMemberships, setGiftedMemberships] = useState([]);
  const [tierId, setTierId] = useState('');
  const [durationMonths, setDurationMonths] = useState('12');
  const [savingMembership, setSavingMembership] = useState(false);
  const [lastGiftedMembership, setLastGiftedMembership] = useState(null);

  useEffect(() => {
    loadGiftCards();
    loadTiers();
    loadGiftedMemberships();
  }, []);

  const loadGiftCards = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await api.get('/admin/gift-cards');
      setGiftCards(response.giftCards || []);
    } catch (error) {
      setLoadError(true);
      setGiftCards([]);
      toast.error('Failed to load gift cards');
    } finally {
      setLoading(false);
    }
  };

  const loadTiers = async () => {
    try {
      const response = await api.get('/membership-tiers');
      setTiers(response.tiers || []);
    } catch (error) {
      toast.error('Failed to load membership tiers');
    }
  };

  const loadGiftedMemberships = async () => {
    setMembershipsError(false);
    try {
      const response = await api.get('/admin/gifted-memberships');
      setGiftedMemberships(response.giftedMemberships || []);
    } catch (error) {
      setMembershipsError(true);
      setGiftedMemberships([]);
      toast.error('Failed to load gifted memberships');
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    const dollars = parseFloat(amount);
    if (!dollars || dollars <= 0) {
      toast.error('Enter a dollar amount greater than 0');
      return;
    }

    setSaving(true);
    try {
      const amountCents = Math.round(dollars * 100);
      const response = await api.post('/admin/gift-cards', { amountCents });
      const card = response.giftCard;
      setLastGenerated(card);
      setGiftCards((prev) => [card, ...prev]);
      setAmount('');
      toast.success(`Gift card created: ${card.code}`);
    } catch (error) {
      toast.error(error.message || 'Failed to generate gift card');
    } finally {
      setSaving(false);
    }
  };

  // The reason prompt IS the confirmation: Cancel aborts, and an empty reason is
  // refused, so a void needs a deliberate typed justification rather than a
  // single OK click. Card #6238 c3 — the actor comes from the session server-side.
  // PATCH, not POST: the router maps POST -> `gift_cards.create` and
  // PATCH -> `gift_cards.edit`, and voiding is an edit. This is what makes the
  // button's gate and the API's answer agree for a role holding only `.edit`.
  const handleVoid = async (card) => {
    const reason = window.prompt(
      `Void gift card "${card.code}"? This cannot be undone.\n\nReason (required, recorded against your account):`
    );
    if (reason === null) return;
    if (reason.trim() === '') {
      toast.error('A reason is required to void a gift card');
      return;
    }
    try {
      const response = await api.patch(`/admin/gift-cards/${card.id}/void`, {
        reason: reason.trim()
      });
      const updated = response.giftCard;
      setGiftCards((prev) => prev.map((c) => (c.id === card.id ? updated : c)));
      toast.success('Gift card voided');
    } catch (error) {
      toast.error(error.message || 'Failed to void gift card');
    }
  };

  const handleCopy = async (code) => {
    const ok = await copyToClipboard(code);
    if (ok) toast.success('Code copied to clipboard');
    else toast.error('Failed to copy code');
  };

  const giftUrlFor = (code) => `${window.location.origin}/gift/${code}`;

  const handleCopyUrl = async (code) => {
    const ok = await copyToClipboard(giftUrlFor(code));
    if (ok) toast.success('Shareable link copied to clipboard');
    else toast.error('Failed to copy link');
  };

  const handleGenerateMembership = async (e) => {
    e.preventDefault();
    if (!tierId) {
      toast.error('Select a membership tier');
      return;
    }
    const months = parseInt(durationMonths, 10);
    if (!Number.isInteger(months) || months <= 0) {
      toast.error('Enter a duration of at least 1 month');
      return;
    }

    setSavingMembership(true);
    try {
      const response = await api.post('/admin/gifted-memberships', {
        membershipTierId: tierId,
        durationMonths: months,
      });
      const gift = response.giftedMembership;
      setLastGiftedMembership(gift);
      setGiftedMemberships((prev) => [gift, ...prev]);
      toast.success(`Gifted membership created: ${gift.shortCode}`);
    } catch (error) {
      toast.error(error.message || 'Failed to generate gifted membership');
    } finally {
      setSavingMembership(false);
    }
  };

  const handleVoidMembership = async (gift) => {
    if (!window.confirm(`Void gifted membership "${gift.shortCode}"? This cannot be undone.`)) return;
    try {
      const response = await api.post(`/admin/gifted-memberships/${gift.id}/void`);
      const updated = response.giftedMembership;
      setGiftedMemberships((prev) => prev.map((g) => (g.id === gift.id ? { ...g, ...updated } : g)));
      toast.success('Gifted membership voided');
    } catch (error) {
      toast.error(error.message || 'Failed to void gifted membership');
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>Loading gift cards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {showCredit && (
        <>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              Gift <span className={styles.accentText}>Cards</span>
            </h1>
            <p className={styles.subtitle}>
              Generate gift cards customers can redeem for account credit
            </p>
          </div>
        </div>

        {/* Generate Form — hidden entirely without gift_cards.create (#6238 c4) */}
        {canCreateGift && (
        <form className={styles.generatePanel} onSubmit={handleGenerate}>
          <div className={styles.generateField}>
            <label htmlFor="giftAmount">Amount (USD)</label>
            <div className={styles.amountInputWrapper}>
              <span className={styles.dollarSign}>$</span>
              <input
                id="giftAmount"
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g., 25.00"
                className={styles.amountInput}
              />
            </div>
          </div>
          <button type="submit" className={styles.generateBtn} disabled={saving}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {saving ? 'Generating...' : 'Generate Gift Card'}
          </button>
        </form>
        )}

        {/* Last Generated Banner */}
        {lastGenerated && (
          <div className={styles.generatedBanner}>
            <div className={styles.generatedInfo}>
              <span className={styles.generatedLabel}>New gift card generated</span>
              <span className={styles.generatedCode}>{lastGenerated.code}</span>
              <span className={styles.generatedAmount}>{formatPrice(lastGenerated.amountCents)}</span>
            </div>
            <button
              type="button"
              className={styles.copyBtn}
              onClick={() => handleCopy(lastGenerated.code)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy
            </button>
          </div>
        )}

        {/* Table */}
        <div className={styles.tableWrapper}>
          {loadError ? (
            <ListLoadError message="Could not load gift cards" onRetry={loadGiftCards} />
          ) : giftCards.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M2 11h20" />
                <path d="M12 7V21" />
                <path d="M7.5 7a2.5 2.5 0 0 1 0-5C10 2 12 7 12 7" />
                <path d="M16.5 7a2.5 2.5 0 0 0 0-5C14 2 12 7 12 7" />
              </svg>
              <h3>No gift cards yet</h3>
              <p>Generate your first gift card to offer account credit to customers.</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Redeemed / Voided</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {giftCards.map((card) => (
                  <tr key={card.id}>
                    <td>
                      <span className={styles.codeCell}>{card.code}</span>
                    </td>
                    <td>
                      <span className={styles.amountBadge}>{formatPrice(card.amountCents)}</span>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${getStatusClass(card.status)}`}>
                        {card.status}
                      </span>
                    </td>
                    <td>{formatDate(card.createdAt)}</td>
                    <td>
                      {card.status === 'redeemed' && (
                        <div className={styles.redeemedInfo}>
                          <span>{card.redeemedByEmail || 'Unknown'}</span>
                          <span className={styles.muted}>{formatDate(card.redeemedAt)}</span>
                        </div>
                      )}
                      {/* #6238 c3: the void's actor and reason, read back from the row. */}
                      {card.status === 'void' && (
                        <div className={styles.redeemedInfo}>
                          <span>{card.voidedByEmail || card.voidedBy || 'Unknown'}</span>
                          <span className={styles.muted}>{formatDate(card.voidedAt)}</span>
                          {card.voidReason && (
                            <span className={styles.muted} title={card.voidReason}>
                              {card.voidReason}
                            </span>
                          )}
                        </div>
                      )}
                      {card.status !== 'redeemed' && card.status !== 'void' && (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      {card.status === 'active' && canVoidGift ? (
                        <button
                          className={`${styles.actionBtn} ${styles.voidBtn}`}
                          onClick={() => handleVoid(card)}
                          title="Void"
                        >
                          Void
                        </button>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
      )}

      {showMemberships && (
        <>
        {/* ============================================ */}
        {/* Gift a Free Membership */}
        {/* ============================================ */}
        {showCredit && <div className={styles.sectionDivider} />}

        <div className={styles.header}>
          <div>
            <h2 className={styles.sectionTitle}>
              Gift a Free <span className={styles.accentText}>Membership</span>
            </h2>
            <p className={styles.subtitle}>
              Generate a shareable link that grants a complimentary membership — no billing
            </p>
          </div>
        </div>

        {/* Generate Membership Form */}
        {canCreateGift && (
        <form className={styles.generatePanel} onSubmit={handleGenerateMembership}>
          <div className={styles.generateField}>
            <label htmlFor="giftTier">Membership Tier</label>
            <select
              id="giftTier"
              value={tierId}
              onChange={(e) => setTierId(e.target.value)}
              className={styles.tierSelect}
            >
              <option value="">Select a tier…</option>
              {tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                  {tier.price ? ` — ${formatPrice(tier.price)}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.generateField}>
            <label htmlFor="giftDuration">Duration (months)</label>
            <input
              id="giftDuration"
              type="number"
              min="1"
              step="1"
              value={durationMonths}
              onChange={(e) => setDurationMonths(e.target.value)}
              placeholder="12"
              className={styles.durationInput}
            />
          </div>
          <button type="submit" className={styles.generateBtn} disabled={savingMembership}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {savingMembership ? 'Generating...' : 'Generate Membership'}
          </button>
        </form>
        )}

        {/* Last Generated Membership Banner */}
        {lastGiftedMembership && (
          <div className={styles.generatedBanner}>
            <div className={styles.generatedInfo}>
              <span className={styles.generatedLabel}>New membership gift generated</span>
              <span className={styles.generatedCode}>{lastGiftedMembership.shortCode}</span>
              <span className={styles.generatedAmount}>
                {lastGiftedMembership.tierName} · {lastGiftedMembership.durationMonths} mo
              </span>
              <span className={styles.generatedUrl}>{giftUrlFor(lastGiftedMembership.shortCode)}</span>
            </div>
            <div className={styles.bannerActions}>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={() => handleCopy(lastGiftedMembership.shortCode)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy Code
              </button>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={() => handleCopyUrl(lastGiftedMembership.shortCode)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                Copy Link
              </button>
            </div>
          </div>
        )}

        {/* Gifted Memberships Table */}
        <div className={styles.tableWrapper}>
          {membershipsError ? (
            <ListLoadError
              message="Could not load gifted memberships"
              onRetry={loadGiftedMemberships}
            />
          ) : giftedMemberships.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M2 11h20" />
                <path d="M12 7V21" />
                <path d="M7.5 7a2.5 2.5 0 0 1 0-5C10 2 12 7 12 7" />
                <path d="M16.5 7a2.5 2.5 0 0 0 0-5C14 2 12 7 12 7" />
              </svg>
              <h3>No gifted memberships yet</h3>
              <p>Generate a complimentary membership to share with someone special.</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Tier</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Redeemed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {giftedMemberships.map((gift) => (
                  <tr key={gift.id}>
                    <td>
                      <span className={styles.codeCell}>{gift.shortCode}</span>
                    </td>
                    <td>
                      <span className={styles.tierBadge}>{gift.tierName || '—'}</span>
                    </td>
                    <td>{gift.durationMonths} mo</td>
                    <td>
                      <span className={`${styles.statusBadge} ${getStatusClass(gift.status)}`}>
                        {gift.status}
                      </span>
                    </td>
                    <td>{formatDate(gift.createdAt)}</td>
                    <td>
                      {gift.status === 'redeemed' ? (
                        <div className={styles.redeemedInfo}>
                          <span>{gift.redeemedByEmail || 'Unknown'}</span>
                          <span className={styles.muted}>{formatDate(gift.redeemedAt)}</span>
                        </div>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      {gift.status === 'active' && canVoidGift ? (
                        <button
                          className={`${styles.actionBtn} ${styles.voidBtn}`}
                          onClick={() => handleVoidMembership(gift)}
                          title="Void"
                        >
                          Void
                        </button>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default GiftCardsPage;
